// Fan CRM hooks (marketing/fans) — the fan directory + stats strip, the Fan
// Detail overlay source, and the manual add/edit/delete, hand-written over the generated client via the tenant-keyed
// wrappers. Keys are ['band', bandId, 'fans'] + ['band', bandId, 'fan', fanId] so a band switch scorches
// them. There is NO fan UI-signal (fan PII never rides plane B), so mutations invalidate locally on success. The
// whole surface is fans:read / fans:manage (the audience — owner/AV + management ONLY; tighter than the
// Marketing tab's releases:read, since fan PII is gated from label + content).
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type FanDirectoryRead = components["schemas"]["FanDirectoryRead"];
export type FanSummary = components["schemas"]["FanSummary"];
export type FanStats = components["schemas"]["FanStats"];
export type FanView = components["schemas"]["FanView"];
export type MetroHint = components["schemas"]["MetroHint"];
export type AddFanRequest = components["schemas"]["AddFanRequest"];
export type UpdateFanRequest = components["schemas"]["UpdateFanRequest"];
export type FanImportResult = components["schemas"]["FanImportResult"];
export type SignupQrView = components["schemas"]["SignupQrView"];

/** GET the fan directory + stats strip (fans:read). Optional `search`; first page (cursor pagination is the
 *  "Load more" path — kept simple here for the first page which carries the stats). */
export function useFans(search?: string) {
  const { api } = useApp();
  return useBandQuery<FanDirectoryRead>(
    ["fans", { search: search || null }],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/fans", {
        params: { path: { bandId }, query: search ? { search } : undefined },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/** GET one fan's detail (fans:read) — the overlay source; enabled only when a fan is open. */
export function useFanDetail(fanId?: string) {
  const { api } = useApp();
  return useBandQuery<FanView>(
    ["fan", fanId ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/fans/{fanId}", {
        params: { path: { bandId, fanId: fanId! } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: !!fanId },
  );
}

/** GET the live metro-rollup hint (fans:read). Debounce the (city,state) at the call site; enabled only
 *  when a city is present. The hint is SERVER-computed so it always matches what a save will store. */
export function useMetroHint(city: string, state: string) {
  const { api } = useApp();
  return useBandQuery<MetroHint>(
    ["fan-metro-hint", { city, state }],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/fans/metro-hint", {
        params: { path: { bandId }, query: { city, state } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: city.trim().length > 0, staleTime: 5 * 60_000 },
  );
}

/** POST a new fan (fans:manage). Never optimistic. Invalidates the directory on success. */
export function useAddFan() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<FanView, AddFanRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/fans", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "fans"] }) },
  );
}

/** PUT an edit (fans:manage). Invalidates the directory AND the open fan detail on success. */
export function useUpdateFan() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<FanView, { fanId: string; body: UpdateFanRequest }>(
    async (band, { fanId, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/fans/{fanId}", {
        params: { path: { bandId: band, fanId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (view) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "fans"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "fan", view.fanId] });
      },
    },
  );
}

/** POST an import (fans:manage) — a CSV upload, as multipart/form-data. Idempotent
 *  server-side (ux_fans_band_email), so a re-run never duplicates. Invalidates the directory on success (no
 *  fan UI-signal — fan PII never rides plane B). Errors are surfaced by the caller (a 400 no-column / 502 vendor). */
export function useImportFans() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<FanImportResult, { file?: File | null }>(
    async (band, { file }) => {
      // Build the multipart body directly; openapi-fetch omits Content-Type for a FormData body (browser sets the
      // boundary). Keys match the FanImportForm model (case-insensitive server-side).
      const fd = new FormData();
      if (file) fd.set("File", file);
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/fans/import", {
        params: { path: { bandId: band } },
        body: fd as never,
        bodySerializer: (b) => b as BodyInit,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "fans"] }) },
  );
}

/** GET the printable signup QR (fans:read) — `{ signupUrl, qrSvgDataUri }`, generated IN-HOUSE server-side
 *  (DROP-19). Enabled only when the QR modal is open. */
export function useSignupQr(enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<SignupQrView>(
    ["fan-signup-qr"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/fans/signup-qr", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled, staleTime: 5 * 60_000 },
  );
}

/** DELETE a fan — soft delete server-side (idempotent 204). Invalidates the directory on success. */
export function useDeleteFan() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { fanId: string }>(
    async (band, { fanId }) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/fans/{fanId}", {
        params: { path: { bandId: band, fanId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "fans"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't remove the fan")),
    },
  );
}
